import { div, h1, p, button, canvas, textarea, fnstate, pathParameters, goTo } from '@srfnstack/fntags'

const TARGET_LABEL = { printify: 'Printify', etsy: 'Etsy' }
// Keys that never produce a beforeinput event, so must be caught via keydown.
const NAV_KEYS = ['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Escape']

// Streamed in-browser login. CDP screencast of a server-side browser sized to
// THIS device (mobile gets a readable mobile layout), driven by tap/type.
//
// Text input bridge: a hidden <textarea> is focused on canvas tap (summons the
// mobile keyboard). We forward via the `beforeinput` event — unlike keydown,
// it fires reliably on mobile soft keyboards for insert / delete / enter / paste.
// The textarea accumulates (not preventDefault'd) so deletions have content to
// act on. A Paste button uses the clipboard API since you can't long-press an
// invisible field.
export const IntegrationConnect = () => {
  const target = pathParameters().target
  const label = TARGET_LABEL[target] || target
  const status = fnstate('starting')
  const message = fnstate('Starting a secure login session…')

  const isMobile = window.matchMedia && window.matchMedia('(pointer: coarse)').matches
  const w = Math.max(320, Math.min(Math.round(window.innerWidth || 1024), 1600))
  const h = Math.max(480, Math.min(Math.round(window.innerHeight || 800), 1400))

  const canvasEl = canvas({
    width: w,
    height: h,
    class: 'block w-full border border-base-300 rounded bg-base-100',
    style: `touch-action:none; aspect-ratio:${w}/${h};`
  })
  const kbd = textarea({
    autocapitalize: 'off',
    autocomplete: 'off',
    autocorrect: 'off',
    spellcheck: 'false',
    'aria-hidden': 'true',
    style: 'position:absolute; top:0; left:0; height:1px; width:1px; opacity:0; pointer-events:none; font-size:16px; border:0; padding:0; resize:none;'
  })

  let ws = null
  const send = (o) => { if (ws && ws.readyState === 1) { try { ws.send(JSON.stringify(o)) } catch (_) {} } }
  const posOf = (e) => {
    const r = canvasEl.getBoundingClientRect()
    return { x: Math.round((e.clientX - r.left) * (canvasEl.width / r.width)), y: Math.round((e.clientY - r.top) * (canvasEl.height / r.height)) }
  }
  const focusKbd = () => { try { kbd.focus({ preventScroll: true }) } catch (_) { kbd.focus() } }

  const doPaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (text) send({ type: 'text', text })
    } catch (_) { window.alert('Could not read the clipboard. Tap a field, then use your keyboard\'s paste.') }
  }

  const wireInput = () => {
    let buttons = 0
    canvasEl.addEventListener('pointerdown', (e) => { e.preventDefault(); buttons = 1; const pt = posOf(e); send({ type: 'mouse', eventType: 'mousePressed', button: 'left', clickCount: 1, buttons, ...pt }); focusKbd() })
    canvasEl.addEventListener('pointerup', (e) => { e.preventDefault(); buttons = 0; const pt = posOf(e); send({ type: 'mouse', eventType: 'mouseReleased', button: 'left', clickCount: 1, buttons, ...pt }) })
    canvasEl.addEventListener('pointermove', (e) => { const pt = posOf(e); send({ type: 'mouse', eventType: 'mouseMoved', buttons, ...pt }) })
    canvasEl.addEventListener('wheel', (e) => { e.preventDefault(); const pt = posOf(e); send({ type: 'wheel', ...pt, deltaX: e.deltaX, deltaY: e.deltaY }) }, { passive: false })

    // beforeinput fires reliably on mobile for every edit type, including paste.
    kbd.addEventListener('beforeinput', (e) => {
      const t = e.inputType || ''
      if (t === 'insertText' || t === 'insertCompositionText' || t === 'insertReplacementText') {
        if (e.data != null) send({ type: 'text', text: e.data })
      } else if (t === 'insertFromPaste' || t === 'insertFromPasteAsQuotation') {
        const pasted = (e.dataTransfer && e.dataTransfer.getData('text')) || e.data || ''
        if (pasted) send({ type: 'text', text: pasted })
      } else if (t === 'insertLineBreak' || t === 'insertParagraph') {
        send({ type: 'key', key: 'Enter' })
        kbd.value = ''
      } else if (t.startsWith('delete')) {
        send({ type: 'key', key: 'Backspace' })
      }
    })
    // Nav/control keys don't fire beforeinput — catch them here.
    kbd.addEventListener('keydown', (e) => { if (NAV_KEYS.includes(e.key)) { e.preventDefault(); send({ type: 'key', key: e.key }) } })
    // Explicit paste event as a second path (desktop Ctrl/Cmd-V into the field).
    kbd.addEventListener('paste', (e) => {
      const text = (e.clipboardData || window.clipboardData)?.getData('text')
      if (text) { e.preventDefault(); send({ type: 'text', text }) }
    })
  }

  const start = async () => {
    try {
      const startRes = await fetch(`/api/system/integrations/connect/${target}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ width: w, height: h, mobile: isMobile })
      })
      if (!startRes.ok) { const e = await startRes.json().catch(() => ({})); status('error'); message(e.error || 'Could not start the login session.'); return }
      const { streamSessionId } = await startRes.json()
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      ws = new window.WebSocket(`${proto}://${window.location.host}/api/system/integrations/connect-stream?sid=${encodeURIComponent(streamSessionId)}`)
      const ctx = canvasEl.getContext('2d')
      const img = new window.Image()
      img.onload = () => ctx.drawImage(img, 0, 0, canvasEl.width, canvasEl.height)
      ws.onopen = () => { status('streaming'); message(`Sign in to ${label} below — tap a field to type. Use the Paste button for saved passwords. Pop-ups are followed automatically.`) }
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data) } catch { return }
        if (m.type === 'frame') { img.src = 'data:image/jpeg;base64,' + m.data } else if (m.type === 'success') {
          status('success'); message('Connected! Redirecting to Settings…'); try { ws.close() } catch (_) {}; window.setTimeout(() => goTo('/settings'), 1600)
        } else if (m.type === 'error') { status('error'); message(m.message || 'Login session error.') } else if (m.type === 'closed') {
          if (status() !== 'success') { status('error'); message('Login session closed. Please try again.') }
        }
      }
      ws.onclose = () => { if (status() === 'streaming' || status() === 'starting') { status('error'); message('Connection closed. Please try again.') } }
      wireInput()
    } catch (err) { status('error'); message(err.message) }
  }

  const cancel = () => { send({ type: 'cancel' }); try { ws && ws.close() } catch (_) {}; goTo('/settings') }

  start()

  return div({ class: 'p-4' },
    h1({ class: 'text-2xl font-bold mb-1' }, `Connect ${label}`),
    p({ class: 'mb-3 text-sm opacity-80' }, message.bindAs(m => m)),
    div({ class: 'relative mb-3' }, canvasEl, kbd),
    div({ class: 'flex gap-2' },
      button({ class: 'btn btn-outline btn-sm', onclick: () => { focusKbd(); send({ type: 'key', key: 'Backspace' }) } }, '⌫ Backspace'),
      button({ class: 'btn btn-outline btn-sm', onclick: () => { focusKbd(); send({ type: 'key', key: 'Enter' }) } }, '⏎ Enter'),
      button({ class: 'btn btn-outline btn-sm', onclick: doPaste }, '📋 Paste'),
      button({ class: 'btn btn-ghost btn-sm ml-auto', onclick: cancel }, status.bindAs(s => s === 'success' ? 'Done' : 'Cancel'))
    )
  )
}
